import { useMemo, useEffect, useCallback } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateRange } from 'react-day-picker';
import { useForm, useWatch } from 'react-hook-form';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { useSimpleModal } from '@/features/_shared/hooks/use-handle-local-modal';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { useListTicketMemberQuery } from '../../../api/get-ticket-member.api';
import { useListLabelTicketQuery } from '@/features/ticketings/label-ticketing/api/get-list-label-ticket.api';
import { useListCaseQuery } from '@/features/cases/case/api/get-list-case.api';
import { useListSpacesQuery } from '@/features/ticketings/spaces/space/api/get-list-space.api';
import { useGetListColumnQuery } from '@/features/ticketings/columns/column/api/get-list-column.api';
import { SINGLE_CASE_QUERY_KEY } from '@/features/cases/case/api/get-single-case.api';

import {
  CreateTicketValidationSchema,
  TCreateTicketValidation,
  useCreateTicketMutation,
} from '../../../api/create-ticket.api';
import { ICreateTicketModalProps } from './create-ticket-modal';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { useCreateTicketAttachments } from './use-create-ticket-attachments';

export function useCreateTicketModal({
  triggerFrom = 'space',
  caseId,
  spaceId,
  spaceColumnId,
  position,
}: ICreateTicketModalProps) {
  // ===== Hooks =====
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();

  // ===== Form =====
  const form = useForm<TCreateTicketValidation>({
    resolver: zodResolver(CreateTicketValidationSchema),
    mode: 'onChange',
    defaultValues: {
      spaceId: '',
      spaceColumnId: '',
      position: '',
      title: '',
      startDate: undefined,
      endDate: undefined,
      content: undefined,
      labelTicketIds: [],
      userIds: [],
      caseIds: [],
      attachments: [],
    },
  });

  // Watch form values to avoid multiple form.watch() calls
  const watchedSpaceId = useWatch({ control: form.control, name: 'spaceId' });

  // ===== Date Modal =====
  const {
    values: dateValues,
    isOpen: isOpenDateModal,
    handleSetValuesModal: handleSetDateValuesModal,
    handleOpenModal: handleOpenDateModal,
    handleCloseModal: handleCloseDateModal,
  } = useSimpleModal<DateRange | null>({
    from: undefined,
    to: undefined,
  });

  // ===== Attachments Hook =====
  const attachments = useCreateTicketAttachments({ form });

  // ===== Data Queries =====
  // Spaces
  const { data: listSpacesData, isLoading: isLoadingListSpacesData } = useListSpacesQuery({
    params: { page: 1, limit: 500 },
    queryConfig: { enabled: triggerFrom === 'case' },
    select: (data) => data.payload || [],
  });

  // Space Columns
  const { data: listSpaceColumnsData, isLoading: isLoadingListSpaceColumnsData } = useGetListColumnQuery({
    params: { spaceId: watchedSpaceId || '' },
    queryConfig: { enabled: triggerFrom === 'case' && !!watchedSpaceId },
    select: (data) => data.payload || [],
  });

  // Cases
  const { data: listCaseData, isLoading: isLoadingListCaseData } = useListCaseQuery({
    params: { page: 1, limit: 500, search: '' },
    queryConfig: { enabled: !!watchedSpaceId },
    select: (data) => data.payload?.rows || [],
  });

  // Ticketing Members
  const { data: listTicketMemberData, isLoading: isLoadingListTicketMemberData } = useListTicketMemberQuery({
    params: { spaceId: watchedSpaceId || '', page: 1, limit: 500 },
    queryConfig: { enabled: !!watchedSpaceId },
    select: (data) => data.payload?.rows || [],
  });

  // Label Tickets
  const { data: listLabelTicketData, isLoading: isLoadingListLabelTicketData } = useListLabelTicketQuery({
    params: { page: 1, limit: 500 },
    queryConfig: { enabled: !!watchedSpaceId },
    select: (data) => data.payload?.rows || [],
  });

  // ===== Mutations =====
  // Create Ticket
  const mutationCreateTicket = useCreateTicketMutation({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error add ticket, please try again';
        const displayMessage = Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage;

        showToast({ message: displayMessage, type: 'error' });
      },
      onSuccess: async () => {
        showToast({ message: 'Ticket added successfully!', type: 'success' });
        modalActions.closeModal('CreateTicketModal');
        form.reset();
        attachments.clearFile();

        // Invalidate queries if triggered from case
        if (triggerFrom === 'case' && caseId) {
          await queryClient.invalidateQueries({
            queryKey: [SINGLE_CASE_QUERY_KEY, caseId],
          });
        }
      },
    },
  });

  // ===== Modal State =====
  const isCreateTicketOpen = useMemo(() => {
    return modalActions.checkIsOpenModal('CreateTicketModal');
  }, [modalActions]);

  // ===== Effects =====
  // Reset form when initial values change
  useEffect(() => {
    const hasInitialValues = Boolean(spaceColumnId || position || spaceId || caseId);

    if (hasInitialValues) {
      form.reset({
        caseIds: caseId ? [caseId] : [],
        spaceColumnId: spaceColumnId || undefined,
        spaceId: spaceId || undefined,
        position: position || undefined,
      });
      attachments.clearFile();
    }
  }, [spaceColumnId, position, spaceId, caseId, form, attachments.clearFile]);

  // ===== Handlers =====
  // Close Modal
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('CreateTicketModal');
  }, [modalActions]);

  // Submit Form
  const handleSubmitForm = useCallback(
    (data: TCreateTicketValidation) => {
      mutationCreateTicket.mutate(data);
    },
    [mutationCreateTicket]
  );

  // ===== Return =====
  return {
    // Modal state
    isCreateTicketOpen,
    handleCloseModal,

    // Form
    triggerFrom,
    form,
    handleSubmitForm,
    isLoadingCreateTicket: Boolean(mutationCreateTicket?.isPending),

    // Date modal
    dateValues,
    isOpenDateModal,
    handleOpenDateModal,
    handleCloseDateModal,
    handleSetDateValuesModal,

    // Spaces
    listSpacesOptions: listSpacesData,
    isLoadingListSpacesData,

    // Space columns
    listSpaceColumnsOptions: listSpaceColumnsData,
    isLoadingListSpaceColumnsData,

    // Space members
    listTicketMemberOptions: listTicketMemberData,
    isLoadingListTicketMemberData,

    // Cases
    listCaseOptions: listCaseData,
    isLoadingListCaseData,

    // Label tickets
    listLabelTicketOptions: listLabelTicketData,
    isLoadingListLabelTicketData,

    // Files
    extractedAttachments: attachments.extractedAttachments,
    uploadingFileIds: attachments.uploadingFileIds,
    isUploadingFiles: attachments.isUploadingFiles,
    handleAddFiles: attachments.handleAddFiles,
    handleRemoveFile: attachments.handleRemoveFile,
  };
}

export type UseCreateTicketModalReturn = ReturnType<typeof useCreateTicketModal>;
